import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { adminApi } from '../../api/client';
import { formatCurrency } from '../../lib/currency';

// ── Editorial readiness badge styles ──────────────────────────────────
const STATUS_STYLE = {
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  ready_to_publish: 'bg-sky-100 text-sky-700 border-sky-200',
  needs_content: 'bg-amber-100 text-amber-800 border-amber-200',
  needs_price: 'bg-amber-100 text-amber-800 border-amber-200',
  needs_image: 'bg-amber-100 text-amber-800 border-amber-200',
  ops_only: 'bg-slate-100 text-slate-700 border-slate-200',
  incomplete: 'bg-rose-100 text-rose-700 border-rose-200',
};

const STATUS_LABEL = {
  published: 'Published',
  ready_to_publish: 'Ready',
  needs_content: 'Needs content',
  needs_price: 'Needs price',
  needs_image: 'Needs image',
  ops_only: 'Ops-only',
  incomplete: 'Incomplete',
};

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'published', label: 'Published' },
  { key: 'ready_to_publish', label: 'Ready to publish' },
  { key: 'needs_content', label: 'Needs content' },
  { key: 'needs_price', label: 'Needs price' },
  { key: 'needs_image', label: 'Needs image' },
  { key: 'ops_only', label: 'Ops-only / imported' },
];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [readinessByProduct, setReadinessByProduct] = useState({});
  const [readinessSummary, setReadinessSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name'); // 'name' | 'readiness'

  // Stable loader: `adminApi` is module-level and all setters keep
  // their identity, so `[]` deps are correct (not a shortcut).
  const loadProducts = useCallback(() => {
    Promise.all([
      adminApi.listProducts(),
      adminApi.productReadiness().catch(() => ({ data: { items: [], summary: null } })),
    ]).then(([prods, ready]) => {
      setProducts(prods.data || []);
      const byId = {};
      (ready.data?.items || []).forEach(it => { byId[it._id] = it; });
      setReadinessByProduct(byId);
      setReadinessSummary(ready.data?.summary || null);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const deleteProduct = async (id) => {
    if (!window.confirm('Product verwijderen?')) return;
    await adminApi.deleteProduct(id);
    setProducts(prev => prev.filter(p => p._id !== id));
  };

  const enriched = useMemo(() => {
    return products.map(p => ({
      ...p,
      readiness: readinessByProduct[p._id] || null,
    }));
  }, [products, readinessByProduct]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = enriched.filter(p => {
      if (q) {
        const inName = (p.name_nl || '').toLowerCase().includes(q);
        const inSku = (p.sku || '').toLowerCase().includes(q);
        if (!inName && !inSku) return false;
      }
      if (filter !== 'all') {
        const status = p.readiness?.readinessStatus;
        if (status !== filter) return false;
      }
      return true;
    });
    if (sortBy === 'readiness') {
      const order = ['needs_content', 'needs_price', 'needs_image', 'incomplete', 'ops_only', 'ready_to_publish', 'published'];
      rows = rows.slice().sort((a, b) => {
        const ai = order.indexOf(a.readiness?.readinessStatus || 'incomplete');
        const bi = order.indexOf(b.readiness?.readinessStatus || 'incomplete');
        if (ai !== bi) return ai - bi;
        return (a.name_nl ?? '').localeCompare(b.name_nl ?? '', 'nl', { sensitivity: 'base' });
      });
    } else {
      rows = rows.slice().sort((a, b) => (a.name_nl ?? '').localeCompare(b.name_nl ?? '', 'nl', { sensitivity: 'base' }));
    }
    return rows;
  }, [enriched, search, filter, sortBy]);

  const counts = readinessSummary || { published: 0, readyToPublish: 0, needsWork: 0, opsOnly: 0 };

  return (
    <div data-testid="admin-products-page" className="p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl font-light text-foreground">Producten</h1>
          <p className="text-sm text-muted-foreground mt-1">{products.length} producten in catalogus</p>
        </div>
        <div className="flex items-center gap-2">
        <Link
          to="/admin/products/new"
          data-testid="new-product-btn"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 shadow-sm"
        >
          <Plus className="w-4 h-4" /> Nieuw product
        </Link>
        <Link
          to="/admin/products/legacy-shop"
          data-testid="legacy-shop-import-btn"
          className="inline-flex items-center gap-2 bg-card text-foreground border border-border rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-muted/40 transition-all"
          title="Scrape public product content from theherborist.be and stage it for review"
        >
          Legacy Shop Import
        </Link>
        </div>
      </div>

      {/* Editorial readiness summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <SummaryCard label="Published" value={counts.published} accent="emerald" />
        <SummaryCard label="Ready to publish" value={counts.readyToPublish} accent="sky" />
        <SummaryCard label="Needs work" value={counts.needsWork} accent="amber" />
        <SummaryCard label="Ops-only / imported" value={counts.opsOnly} accent="slate" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {FILTER_TABS.map(t => (
          <FilterChip key={t.key} active={filter === t.key} onClick={() => setFilter(t.key)}>
            {t.label}
          </FilterChip>
        ))}
        <span className="ml-auto text-[11px] text-muted-foreground">
          Sort:&nbsp;
          <button data-testid="sort-name" onClick={() => setSortBy('name')}
            className={`underline-offset-2 ${sortBy === 'name' ? 'underline font-semibold text-foreground' : 'hover:underline'}`}>name</button>
          <span className="mx-1">·</span>
          <button data-testid="sort-readiness" onClick={() => setSortBy('readiness')}
            className={`underline-offset-2 ${sortBy === 'readiness' ? 'underline font-semibold text-foreground' : 'hover:underline'}`}>readiness</button>
        </span>
      </div>

      <div className="relative max-w-sm mb-4">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          data-testid="admin-product-search"
          type="text" placeholder="Zoeken op naam of SKU..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-full border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {loading ? (
        <div className="space-y-2">{['s1', 's2', 's3', 's4', 's5', 's6'].map(k => <div key={k} className="h-14 bg-muted rounded-xl animate-pulse" />)}</div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wider">
              <tr>
                <th className="px-5 py-3.5 text-left font-semibold">Naam (NL)</th>
                <th className="px-5 py-3.5 text-left font-semibold">SKU</th>
                <th className="px-5 py-3.5 text-left font-semibold">Status</th>
                <th className="px-5 py-3.5 text-right font-semibold">Prijs incl.</th>
                <th className="px-5 py-3.5 text-right font-semibold">Kostprijs</th>
                <th className="px-5 py-3.5 text-center font-semibold" title="Commerce stock — drives storefront availability & checkout stock validation. Compare vs Active Ants warehouse stock in Ops > Inventory.">
                  Voorraad <span className="text-[10px] font-normal normal-case text-muted-foreground/70">(commerce)</span>
                </th>
                <th className="px-5 py-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(p => {
                const r = p.readiness;
                const status = r?.readinessStatus || (p.isActive ? 'published' : 'incomplete');
                const aaImported = r?.aaMasterImported;
                const aaMapped = r?.aaMapped;
                return (
                  <tr key={p._id} data-testid={`product-row-${p._id}`} className="hover:bg-muted/20 transition-colors">
                    <td className="px-5 py-3.5 font-medium text-foreground">
                      <div className="flex items-center gap-2">
                        <span>{p.name_nl}</span>
                        {aaImported && (
                          <span data-testid={`badge-aa-import-${p._id}`} title="Imported from AA product master export — needs editorial review before publishing"
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">AA import</span>
                        )}
                        {aaMapped ? (
                          <span data-testid={`badge-mapped-${p._id}`} title="AA SKU resolves in inventory mappings bridge"
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Mapped</span>
                        ) : aaImported ? (
                          <span data-testid={`badge-no-bridge-${p._id}`} title="No active inventory mapping points at this SKU yet"
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-50 text-slate-600 border border-slate-200">No bridge</span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-muted-foreground text-xs">
                      <SkuCell sku={p.sku} />
                    </td>
                    <td className="px-5 py-3.5">
                      <ReadinessBadge status={status} reasons={r?.readinessReasons || []} productId={p._id} />
                    </td>
                    <td className="px-5 py-3.5 text-right">{formatCurrency(p.price, 'nl')}</td>
                    <td className="px-5 py-3.5 text-right text-muted-foreground">{p.unitCostExVat != null ? formatCurrency(p.unitCostExVat, 'nl') : '--'}</td>
                    <td className="px-5 py-3.5 text-center font-mono text-xs">{p.stock ?? 0}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/products/${p._id}/edit`}
                          data-testid={`edit-product-${p._id}`}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => deleteProduct(p._id)}
                          data-testid={`delete-product-${p._id}`}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filtered.length && <p className="text-center py-12 text-muted-foreground text-sm">Geen producten gevonden</p>}
        </div>
      )}
    </div>
  );
}

function SkuCell({ sku }) {
  const trimmed = (sku || '').trim();
  if (!trimmed) {
    return (
      <span title="AA push blocked — missing SKU" className="inline-flex items-center gap-1 text-amber-700 font-semibold">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />SKU ontbreekt
      </span>
    );
  }
  const valid = /^[A-Za-z0-9._/\-]+$/.test(trimmed);
  if (!valid) {
    return (
      <span title="AA push blocked — invalid characters in SKU" className="inline-flex items-center gap-1 text-red-700 font-semibold">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" />{trimmed}
      </span>
    );
  }
  return <span>{trimmed}</span>;
}

function ReadinessBadge({ status, reasons, productId }) {
  const cls = STATUS_STYLE[status] || STATUS_STYLE.incomplete;
  const label = STATUS_LABEL[status] || status;
  const title = reasons.length ? `Reasons: ${reasons.join(', ')}` : '';
  return (
    <span
      data-testid={`readiness-${productId}`}
      title={title}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${cls}`}
    >
      {label}
    </span>
  );
}

function SummaryCard({ label, value, accent }) {
  const cls = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    sky: 'bg-sky-50 border-sky-200 text-sky-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  }[accent] || 'bg-card border-border text-foreground';
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-0.5">{value ?? 0}</p>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      data-testid={`filter-${typeof children === 'string' ? children.toLowerCase().replace(/\s+/g, '-') : 'chip'}`}
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
        active
          ? 'bg-foreground text-background border-foreground'
          : 'bg-background text-muted-foreground border-border hover:bg-muted/50'
      }`}
    >
      {children}
    </button>
  );
}
